import { productsEndpoint } from "@/lib/config/environments"

export const getPrice = (currency, sku, countryCode, setError) => {
  return fetch(
    `${productsEndpoint}/products/${sku}/price?currency=${currency}&country=${countryCode}`,
  )
    .then(response => response.json())
    .catch(error => {
      console.log(error)
      return null
    })
}

export const getCartRegisteredUser = (
  userKey,
  setError,
  account,
  onSuccess,
) => {
  fetch(`${productsEndpoint}/carts/`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "x-account-id": account,
      "x-client-source": "MXP",
      Authorization: `Bearer ${userKey}`,
    },
  })
    .then(response => response.json())
    .then(result => {
      if (result && result?.lineItems) {
        if (onSuccess) {
          onSuccess(result)
        }
        localStorage.removeItem("cartId")
      }
    })
    .catch(() => {
      // this will error if the user has no cart and is logged in with valid tokens. Instead of displaying an error message, we insure the cart is null.
      if (onSuccess) {
        onSuccess(null)
      }
    })
}

export const addToCartRegisteredUser = (
  currency,
  sku,
  setError,
  count,
  setCount,
  userKey,
  accountId,
  setIsProcessing,
  onAddToCartSuccess,
  setIsOpen,
  setAddToCartError,
  _setIsMaxQuantityError,
) => {
  setIsProcessing(true)
  fetch(`${productsEndpoint}/carts/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-account-id": accountId,
      Authorization: `Bearer ${userKey}`,
    },
    body: JSON.stringify({
      currency: currency,
      lineItems: [
        {
          sku: sku,
          quantity: count,
        },
      ],
    }),
  })
    .then(response => response.json())
    .then(result => {
      if (result?.errorCode === "PRODUCTS_1506") {
        _setIsMaxQuantityError(true)
        setIsOpen(true)
        setIsProcessing(false)
        return
      }
      if (result?.errorCode) {
        throw new Error(result?.errorDescription)
      }
      if (onAddToCartSuccess) {
        onAddToCartSuccess(result, sku)
      }
      setCount(1)
      setIsProcessing(false)
    })
    .catch(_error => {
      console.log(_error)
      setAddToCartError(true)
      setIsOpen(true)
      setIsProcessing(false)
    })
}

export const updateCartRegisteredUser = (
  currency,
  sku,
  setError,
  count,
  setCount,
  userKey,
  accountId,
  cartInfo,
  setIsProcessing,
  onAddToCartSuccess,
  setIsOpen,
  setSubscriptionTypeErrorNonMerge,
  setAddToCartError,
  _setIsMaxQuantityError,
  isQuantityReplacement,
) => {
  const countAsNum = Number(count)

  const productId = cartInfo?.lineItems?.find(prod => {
    return prod.sku === sku
  })
  const body =
    productId?.sku && productId?.sku === sku
      ? {
          productSku: sku,
          updateAction: "UPDATE_CART_LINEITEM",
          lineItemId: productId?.id,
          quantity: isQuantityReplacement
            ? countAsNum
            : productId?.sku &&
                productId?.sku === sku &&
                countAsNum + productId?.quantity <= 40
              ? countAsNum + productId?.quantity
              : countAsNum,
        }
      : {
          productSku: sku,
          updateAction: "ADD_CART_LINEITEM",
          quantity:
            productId?.sku &&
            productId?.sku === sku &&
            countAsNum + productId?.quantity <= 40
              ? countAsNum + productId?.quantity
              : countAsNum,
        }

  setIsProcessing(true)
  fetch(`${productsEndpoint}/carts/`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "x-account-id": accountId,
      Authorization: `Bearer ${userKey}`,
    },
    body: JSON.stringify({ lineItems: [body] }),
  })
    .then(response => {
      return response.json().then(data => {
        if (data?.errorCode === "PRODUCTS_1506") {
          _setIsMaxQuantityError(true)
          setIsOpen(true)
          setIsProcessing(false)
          return
        } else if (data?.errorCode === "PRODUCTS_1604") {
          setSubscriptionTypeErrorNonMerge(true)
          setIsOpen(true)
          setIsProcessing(false)
          return
        } else if (data?.errors || data?.status || data?.errorCode) {
          throw new Error(response.statusText)
        } else if (onAddToCartSuccess) {
          onAddToCartSuccess(data, sku)
        }
        setCount(1)
        setIsProcessing(false)
        return data
      })
    })
    .catch(() => {
      setAddToCartError(true)
      setIsOpen(true)
      setIsProcessing(false)
    })
}

export const addToCartAnonynous = (
  currency,
  countryCode,
  sku,
  setError,
  count,
  setCount,
  setIsProcessing,
  onAddToCartSuccess,
  setIsOpen,
  setAddToCartError,
  _setIsMaxQuantityError,
) => {
  setIsProcessing(true)
  fetch(`${productsEndpoint}/carts/anonymous`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      currency: currency,
      country: countryCode,
      lineItems: [
        {
          sku: sku,
          quantity: count,
        },
      ],
    }),
  })
    .then(response => response.json())
    .then(result => {
      if (result?.errorCode === "PRODUCTS_1506") {
        _setIsMaxQuantityError(true)
        setIsOpen(true)
        setIsProcessing(false)
        return
      }
      if (result?.errorCode) {
        throw new Error(result?.errorDescription)
      }
      localStorage.setItem("cartId", result?.id)
      if (onAddToCartSuccess) {
        onAddToCartSuccess(result, sku)
      }
      setCount(1)
      setIsProcessing(false)
    })
    .catch(_error => {
      console.log(_error)
      setAddToCartError(true)
      setIsOpen(true)
      setIsProcessing(false)
    })
}

export const getCartForAnonymous = (returningAnonymousUser, setError) => {
  fetch(`${productsEndpoint}/carts/${returningAnonymousUser}`)
    .then(response => {
      if (response?.status === 404 || response?.status === 400) {
        // The user's cart will expire after 7 days and will result in a 404 when passing the anonymous cart id to the endpoint
        // To remedy this without hard coding a 7 day timestamp, we remove the saved cart from localstorage on error
        localStorage.removeItem("cartId")
        return null
      }
      return response.json()
    })
    .then(result => {
      // check for US or CA anonymous cart and then switch to country outside US/CA for projectSight. This removes anonymous cart since API will fail
      if (
        result?.errorCode &&
        result.errorCode === "PRODUCTS_1507" &&
        result?.errorData?.removedSkus
      ) {
        localStorage.removeItem("cartId")
        return
      }
      return result
    })
    .catch(_error => {
      setError(_error)
      console.log(_error)
    })
}

export const updateAnonymousCart = (
  currency,
  sku,
  setError,
  count,
  setCount,
  existingCartForAnonymous,
  cartInfo,
  productId,
  setIsProcessing,
  onAddToCartSuccess,
  setIsOpen,
  setAddToCartError,
  setSubscriptionTypeErrorNonMerge,
  _setIsMaxQuantityError,
  isQuantityReplacement,
) => {
  const countAsNum = Number(count)
  setIsProcessing(true)
  fetch(`${productsEndpoint}/carts/${cartInfo?.id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      lineItems: [{
        productSku: sku,
        updateAction:
          productId?.sku && productId?.sku === sku
            ? "UPDATE_CART_LINEITEM"
            : "ADD_CART_LINEITEM",
        lineItemId: productId?.id,
        currency: currency,
        quantity: isQuantityReplacement
          ? countAsNum
          : productId?.sku &&
              productId?.sku === sku &&
              countAsNum + productId?.quantity <= 40
            ? countAsNum + productId?.quantity
            : countAsNum,
      }]
    }),
  })
    .then(response => response.json())
    .then(result => {
      if (result?.errorCode === "PRODUCTS_1506") {
        _setIsMaxQuantityError(true)
        setIsOpen(true)
        setIsProcessing(false)
        return
      } else if (result?.errorCode === "PRODUCTS_1604") {
        setSubscriptionTypeErrorNonMerge(true)
        setIsOpen(true)
        setIsProcessing(false)
        return
      } else if (result?.errors || result?.status || result?.errorCode) {
        throw new Error(result?.errorDescription)
      }
      if (result?.id) {
        localStorage.setItem("cartId", result?.id)
      }
      if (onAddToCartSuccess) {
        onAddToCartSuccess(result, sku)
      }
      setCount(1)
      setIsProcessing(false)
    })
    .catch(_error => {
      console.log(_error)
      setIsProcessing(false)
      setAddToCartError(true)
      setSubscriptionTypeErrorNonMerge(false)
      setIsOpen(true)
    })
}

export const addToCart = (
  currency,
  countryCode,
  sku,
  setError,
  count,
  setCount,
  existingCartForAnonymous,
  cartInfo,
  accountId,
  userKey,
  setIsProcessing,
  onAddToCartSuccess,
  setIsOpen,
  setSubscriptionTypeErrorNonMerge,
  setAddToCartError,
  setIsMaxQuantityError,
  productUser,
  isQuantityReplacement,
) => {
  // this ensures product users/license admins use anonymous workflows
  if (productUser) {
    if (existingCartForAnonymous && cartInfo?.id) {
      const productId = cartInfo?.lineItems?.find(prod => {
        return prod.sku === sku
      })
      updateAnonymousCart(
        currency,
        sku,
        setError,
        count,
        setCount,
        existingCartForAnonymous,
        cartInfo,
        productId,
        setIsProcessing,
        onAddToCartSuccess,
        setIsOpen,
        setAddToCartError,
        setSubscriptionTypeErrorNonMerge,
        setIsMaxQuantityError,
        isQuantityReplacement,
      )
      return
    } else {
      addToCartAnonynous(
        currency,
        countryCode,
        sku,
        setError,
        count,
        setCount,
        setIsProcessing,
        onAddToCartSuccess,
        setIsOpen,
        setAddToCartError,
        setIsMaxQuantityError,
      )
      return
    }
    // return
  }
  if (!sku) {
    return
  }
  // updates existing anonymous cart (PATCH)
  const cartId = localStorage.getItem("cartId")
  if (existingCartForAnonymous && !accountId && cartId) {
    const productId = cartInfo?.lineItems?.find(prod => {
      return prod.sku === sku
    })
    updateAnonymousCart(
      currency,
      sku,
      setError,
      count,
      setCount,
      existingCartForAnonymous,
      cartInfo,
      productId,
      setIsProcessing,
      onAddToCartSuccess,
      setIsOpen,
      setAddToCartError,
      setSubscriptionTypeErrorNonMerge,
      setIsMaxQuantityError,
      isQuantityReplacement,
    )
    // creates and adds to cart for anonymous (POST)
  } else if (!accountId && !existingCartForAnonymous) {
    addToCartAnonynous(
      currency,
      countryCode,
      sku,
      setError,
      count,
      setCount,
      setIsProcessing,
      onAddToCartSuccess,
      setIsOpen,
      setAddToCartError,
      setIsMaxQuantityError,
      /* eslint-disable-next-line no-undef */
      dispatch,
    )
    // updates existing cart for registered users (PATCH)
  } else if (accountId && cartInfo && cartInfo?.lineItems) {
    updateCartRegisteredUser(
      currency,
      sku,
      setError,
      count,
      setCount,
      userKey,
      accountId,
      cartInfo,
      setIsProcessing,
      onAddToCartSuccess,
      setIsOpen,
      setSubscriptionTypeErrorNonMerge,
      setAddToCartError,
      setIsMaxQuantityError,
      isQuantityReplacement,
    )
    // creates and adds to cart for registered users (POST)
  } else if (accountId) {
    addToCartRegisteredUser(
      currency,
      sku,
      setError,
      count,
      setCount,
      userKey,
      accountId,
      setIsProcessing,
      onAddToCartSuccess,
      setIsOpen,
      setAddToCartError,
      setIsMaxQuantityError,
    )
  }
}
